import { cn } from "@/lib/utils";
import type { FileRecord } from "@cohesive/api-client";
import { Folder } from "lucide-react";
import React from "react";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
  gridCardPreviewPlaceholderIconClassName,
} from "../constants";
import {
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceGridCard } from "../WorkspaceGridCard";

function getFolderName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

export interface WorkspaceFolderCardProps {
  record: FileRecord;
  childCount?: number;
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  /** Registry-backed actions menu rendered in the footer; shows on `group-hover`. */
  menuSlot?: React.ReactNode;
}

export const WorkspaceFolderCard: React.FC<WorkspaceFolderCardProps> = ({
  record,
  childCount = 0,
  isSelected = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  menuSlot,
}) => {
  const folderName = getFolderName(record.path);

  const preview = (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
      <Folder
        className={gridCardPreviewPlaceholderIconClassName}
        strokeWidth={1.75}
      />
    </div>
  );

  const footer = (
    <>
      <div className="min-w-0 flex-1">
        <WorkspaceCardFooterTitleRow
          icon={<WorkspaceCardFooterTypeIcon icon={Folder} />}
        >
          <p className={gridCardFooterTitleClassName}>{folderName}</p>
        </WorkspaceCardFooterTitleRow>
        <p
          className={cn(
            gridCardFooterMetaRowClassName,
            gridCardFooterSubtitleClassName,
          )}
        >
          {childCount === 0
            ? "Empty folder"
            : `${childCount} item${childCount === 1 ? "" : "s"}`}
        </p>
      </div>

      {menuSlot && <div className="shrink-0">{menuSlot}</div>}
    </>
  );

  return (
    <WorkspaceGridCard
      aria-label={folderName}
      aria-selected={isSelected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      preview={preview}
      footer={footer}
      isSelected={isSelected}
      interactive={onClick != null || onDoubleClick != null}
    />
  );
};
